import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Stage, Task } from "../mock/types";
import { BOARD_STAGES } from "../mock/data";
import { TaskCard } from "./TaskCard";

function Column({
	stage,
	tasks,
	onOpenTask,
}: {
	stage: Stage;
	tasks: Task[];
	onOpenTask: (task: Task) => void;
}) {
	return (
		<div className="flex flex-col min-w-[300px] w-[300px] shrink-0 snap-start">
			<div className="flex items-center gap-2 mb-3 px-1">
				<span
					className="h-2.5 w-2.5 rounded-full"
					style={{ backgroundColor: stage.color }}
				/>
				<span className="text-sm font-semibold text-foreground">
					{stage.name}
				</span>
				<span className="ml-auto text-xs font-medium text-muted bg-muted-subtle px-2 py-0.5 rounded-full">
					{tasks.length}
				</span>
			</div>

			<div className="flex-1 flex flex-col gap-2.5 rounded-xl bg-muted-subtle p-2 min-h-[70vh]">
				{tasks.map((task) => (
					<TaskCard
						key={task.id}
						task={task}
						onClick={() => onOpenTask(task)}
					/>
				))}
				{tasks.length === 0 && (
					<div className="flex-1 flex items-center justify-center py-10">
						<p className="text-xs text-muted">No tasks</p>
					</div>
				)}
			</div>
		</div>
	);
}

export function KanbanBoard({
	tasks,
	onOpenTask,
}: {
	tasks: Task[];
	onOpenTask: (task: Task) => void;
}) {
	const scrollRef = useRef<HTMLDivElement>(null);

	const scrollBy = (dir: -1 | 1) => {
		scrollRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
	};

	return (
		<div className="relative">
			<div className="hidden md:flex absolute -top-11 right-0 gap-1">
				<Button
					variant="outline"
					size="icon"
					className="h-8 w-8"
					onClick={() => scrollBy(-1)}
					aria-label="Scroll left"
				>
					<ChevronLeft className="h-4 w-4" />
				</Button>
				<Button
					variant="outline"
					size="icon"
					className="h-8 w-8"
					onClick={() => scrollBy(1)}
					aria-label="Scroll right"
				>
					<ChevronRight className="h-4 w-4" />
				</Button>
			</div>

			<div
				ref={scrollRef}
				className="flex gap-4 overflow-x-auto pb-4 snap-x"
			>
				{BOARD_STAGES.map((stage) => (
					<Column
						key={stage.key}
						stage={stage}
						tasks={tasks.filter((t) => t.status === stage.key)}
						onOpenTask={onOpenTask}
					/>
				))}
			</div>
		</div>
	);
}
